/**
 * Data access for the `changeOrders` collection.
 *
 * Change orders are independent documents keyed by `dealId`. They never mutate
 * the original proposal; their approved deltas feed the contract value ledger
 * (see `contractValue.ts`).
 */

import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  runTransaction,
  updateDoc,
  where,
  Timestamp,
  deleteField,
  type DocumentData,
} from 'firebase/firestore';
import { db } from './firebase';
import type { ChangeOrder, ChangeOrderAuditEntry, ChangeOrderLineItem, ChangeOrderStatus } from '../types';
import { removeUndefinedDeep } from './removeUndefinedDeep';
import { computeChangeOrderBudgetByBucket, splitChangeOrderDelta } from './contractValue';
import { applyProfitMargin } from './proposalCalculations';

/** Firestore rejects NaN/Infinity; normalize and recompute line items before every write. */
function sanitizeLineItems(lineItems: ChangeOrderLineItem[] | undefined): ChangeOrderLineItem[] | undefined {
  if (!lineItems) return undefined;
  return lineItems.map(li => {
    const quantity = Number(li.quantity);
    const unitCost = Number(li.unitCost);
    const margin = Number(li.margin);
    const qty = Number.isFinite(quantity) ? quantity : 0;
    const cost = Number.isFinite(unitCost) ? unitCost : 0;
    const marginPct = Number.isFinite(margin) ? margin : 0;
    const unitPrice = applyProfitMargin(cost, marginPct);
    return {
      id: li.id,
      description: li.description || '',
      bucket: li.bucket || 'unassigned',
      quantity: qty,
      unitCost: cost,
      margin: marginPct,
      unitPrice,
      amount: Math.round(qty * unitPrice),
      costEstimate: Math.round(qty * cost),
    };
  });
}

function sanitizeChangeOrderWrite<T extends Partial<ChangeOrder>>(payload: T): T {
  const next = { ...payload };
  if (next.lineItems) next.lineItems = sanitizeLineItems(next.lineItems);
  if (typeof next.lumpSumAmount === 'number' && !Number.isFinite(next.lumpSumAmount)) {
    next.lumpSumAmount = 0;
  }
  if (typeof next.deltaAmount === 'number' && !Number.isFinite(next.deltaAmount)) {
    next.deltaAmount = 0;
  }
  if (typeof next.builderProfitMargin === 'number' && !Number.isFinite(next.builderProfitMargin)) {
    next.builderProfitMargin = 0;
  }
  return next;
}

/** User-facing message for failed change-order saves. */
export function formatChangeOrderSaveError(err: unknown): string {
  if (err && typeof err === 'object') {
    const code = 'code' in err ? String((err as { code?: string }).code || '') : '';
    const message = 'message' in err ? String((err as { message?: string }).message || '') : '';
    if (code === 'permission-denied') {
      return 'You don’t have permission to save change orders. Ask an admin if you need access.';
    }
    if (message.includes('Document too large') || message.includes('longer than 1048487 bytes')) {
      return 'This change order is too large to save (usually too many imported builder structures). Try line-item mode or fewer structures.';
    }
    if (message) return message;
  }
  return 'Could not save the change order. Please try again.';
}

export const CHANGE_ORDERS_COLLECTION = 'changeOrders';
/** Per-deal monotonic counter so concurrent creates can never collide on a number. */
export const CHANGE_ORDER_COUNTERS_COLLECTION = 'changeOrderCounters';

const FIRESTORE_WRITE_TIMEOUT_MS = 20_000;

function withFirestoreTimeout<T>(promise: Promise<T>, label: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(`${label} timed out after ${FIRESTORE_WRITE_TIMEOUT_MS / 1000}s`)),
      FIRESTORE_WRITE_TIMEOUT_MS,
    );
    promise.then(
      value => {
        clearTimeout(timer);
        resolve(value);
      },
      err => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

/** "CO-001" style display label. */
export function formatChangeOrderNumber(n: number): string {
  return `CO-${String(Math.max(1, Math.floor(n || 1))).padStart(3, '0')}`;
}

/** Searchable reference, e.g. "26-014-CO-001" (project number + CO number). */
export function formatChangeOrderReference(projectNumber: string | undefined, n: number): string {
  const co = formatChangeOrderNumber(n);
  const pn = (projectNumber || '').trim();
  return pn ? `${pn}-${co}` : co;
}

/** Reference for an existing change order (uses the stored value, falling back to a computed one). */
export function getChangeOrderReference(co: Pick<ChangeOrder, 'reference' | 'projectNumber' | 'number'>): string {
  return co.reference || formatChangeOrderReference(co.projectNumber, co.number);
}

/** Public link slug, e.g. "co-deal123-2-x8f3". */
export function generateChangeOrderSlug(dealId: string, number: number): string {
  const salt = Math.random().toString(36).slice(2, 6);
  const dealPart = (dealId || 'co').toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 10);
  return `co-${dealPart}-${number}-${salt}`;
}

function coDoc(snap: { id: string; data: () => unknown }): ChangeOrder {
  return { id: snap.id, ...(snap.data() as ChangeOrder) };
}

/** Next sequential change-order number for a deal (1-based). */
export async function getNextChangeOrderNumber(dealId: string): Promise<number> {
  const snap = await getDocs(query(collection(db, CHANGE_ORDERS_COLLECTION), where('dealId', '==', dealId)));
  let max = 0;
  snap.docs.forEach(d => {
    const n = Number((d.data() as ChangeOrder).number) || 0;
    if (n > max) max = n;
  });
  return max + 1;
}

export async function getChangeOrdersForDeal(dealId: string): Promise<ChangeOrder[]> {
  const snap = await getDocs(query(collection(db, CHANGE_ORDERS_COLLECTION), where('dealId', '==', dealId)));
  return snap.docs.map(coDoc).sort((a, b) => (a.number || 0) - (b.number || 0));
}

/** Live subscription to a deal's change orders (sorted by number). */
export function subscribeChangeOrdersForDeal(
  dealId: string,
  cb: (orders: ChangeOrder[]) => void,
  onError?: (err: unknown) => void,
): () => void {
  const q = query(collection(db, CHANGE_ORDERS_COLLECTION), where('dealId', '==', dealId));
  return onSnapshot(
    q,
    snap => cb(snap.docs.map(coDoc).sort((a, b) => (a.number || 0) - (b.number || 0))),
    err => {
      console.warn('[changeOrders] subscription error', err);
      onError?.(err);
    },
  );
}

export async function getChangeOrderBySlug(slug: string): Promise<ChangeOrder | null> {
  const snap = await getDocs(query(collection(db, CHANGE_ORDERS_COLLECTION), where('slug', '==', slug)));
  if (snap.empty) return null;
  return coDoc(snap.docs[0]);
}

export async function getChangeOrderById(id: string): Promise<ChangeOrder | null> {
  const snap = await getDoc(doc(db, CHANGE_ORDERS_COLLECTION, id));
  if (!snap.exists()) return null;
  return coDoc(snap);
}

export interface CreateChangeOrderInput
  extends Omit<ChangeOrder, 'id' | 'number' | 'slug' | 'status' | 'createdAt' | 'updatedAt' | 'deltaAmount'> {
  status?: ChangeOrderStatus;
}

/**
 * Create a draft change order. Computes the next number, slug, derived delta and
 * P&L budget breakdown.
 *
 * The sequential number is allocated inside a Firestore transaction against a
 * per-deal counter doc (`changeOrderCounters/{dealId}`), so two staff creating
 * change orders for the same deal at the same instant can never receive the same
 * number. The counter is seeded migration-safely from the current max for deals
 * that predate the counter.
 */
export async function createChangeOrder(input: CreateChangeOrderInput): Promise<ChangeOrder> {
  const safeInput = sanitizeChangeOrderWrite({ ...input });
  const deltaSplit = splitChangeOrderDelta({
    pricingMode: safeInput.pricingMode!,
    lumpSumAmount: safeInput.lumpSumAmount,
    lineItems: safeInput.lineItems,
    builderNodeSets: safeInput.builderNodeSets,
    builderProfitMargin: safeInput.builderProfitMargin,
    bondingPercent: safeInput.bondingPercent,
    deltaAmount: 0,
  });
  const deltaAmount = deltaSplit.total;
  // Only stamp bonding fields when the proposal actually bonds — older COs keep their shape.
  const bondingFields = (Number(safeInput.bondingPercent) || 0) > 0
    ? { bondingPercent: Number(safeInput.bondingPercent), bondingAdjustment: deltaSplit.bonding }
    : {};
  const budgetCostByBucket = computeChangeOrderBudgetByBucket({
    pricingMode: safeInput.pricingMode!,
    lineItems: safeInput.lineItems,
    builderNodeSets: safeInput.builderNodeSets,
    budgetCostByBucket: safeInput.budgetCostByBucket,
  });

  // Seed value used only when the counter doc doesn't exist yet (pre-counter deals).
  // Once the counter exists, the atomic increment is the single source of truth.
  const seedMax = (await withFirestoreTimeout(
    getNextChangeOrderNumber(input.dealId),
    'Change order number lookup',
  )) - 1;

  const coRef = doc(collection(db, CHANGE_ORDERS_COLLECTION));
  const counterRef = doc(db, CHANGE_ORDER_COUNTERS_COLLECTION, input.dealId);

  return withFirestoreTimeout(
    runTransaction(db, async tx => {
    const counterSnap = await tx.get(counterRef);
    const base = counterSnap.exists() ? Number(counterSnap.data().count) || 0 : seedMax;
    const number = base + 1;
    const slug = generateChangeOrderSlug(input.dealId, number);

    const audit: ChangeOrderAuditEntry = {
      at: new Date(),
      byUid: input.createdBy,
      action: 'created',
      toStatus: input.status || 'draft',
      amount: deltaAmount,
    };

    const docData = removeUndefinedDeep({
      ...safeInput,
      number,
      slug,
      reference: formatChangeOrderReference(safeInput.projectNumber, number),
      status: safeInput.status || 'draft',
      deltaAmount,
      ...bondingFields,
      budgetCostByBucket,
      auditLog: [audit],
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });

    tx.set(counterRef, { dealId: input.dealId, count: number, updatedAt: Timestamp.now() });
    tx.set(coRef, docData);

    return { id: coRef.id, ...(docData as ChangeOrder) };
    }),
    'Change order create',
  );
}

/**
 * Update a change order. When pricing inputs change, the derived `deltaAmount`
 * and `budgetCostByBucket` are recomputed unless explicitly provided.
 */
export async function updateChangeOrder(
  id: string,
  updates: Partial<ChangeOrder>,
  options?: { recomputeFrom?: ChangeOrder },
): Promise<void> {
  const next = sanitizeChangeOrderWrite({ ...updates });

  const base = options?.recomputeFrom;
  if (base && (updates.pricingMode || updates.lumpSumAmount !== undefined || updates.lineItems || updates.builderNodeSets || updates.builderProfitMargin !== undefined || updates.bondingPercent !== undefined)) {
    const merged = { ...base, ...updates } as ChangeOrder;
    if (next.deltaAmount === undefined) {
      const split = splitChangeOrderDelta(merged);
      next.deltaAmount = split.total;
      if ((Number(merged.bondingPercent) || 0) > 0) {
        next.bondingPercent = Number(merged.bondingPercent);
        next.bondingAdjustment = split.bonding;
      } else if (base.bondingPercent !== undefined || base.bondingAdjustment !== undefined) {
        // Bonding was removed from the proposal — clear the stale pass-through.
        next.bondingPercent = 0;
        next.bondingAdjustment = 0;
      }
    }
    if (next.budgetCostByBucket === undefined) {
      next.budgetCostByBucket = computeChangeOrderBudgetByBucket(merged);
    }
  }

  const patch: DocumentData = {
    ...next,
    updatedAt: Timestamp.now(),
  };

  await withFirestoreTimeout(
    updateDoc(
      doc(db, CHANGE_ORDERS_COLLECTION, id),
      removeUndefinedDeep(patch),
    ),
    'Change order save',
  );
}

export async function appendChangeOrderAudit(id: string, entry: ChangeOrderAuditEntry, existing: ChangeOrderAuditEntry[] = []): Promise<void> {
  await updateDoc(doc(db, CHANGE_ORDERS_COLLECTION, id), {
    auditLog: [...existing, { ...entry, at: Timestamp.fromDate(entry.at instanceof Date ? entry.at : new Date()) }],
    updatedAt: Timestamp.now(),
  });
}

/** Pull a released (sent) change order back to draft — removes it from the client portal. */
export async function retractChangeOrder(
  co: ChangeOrder,
  actor: { uid: string; name?: string },
): Promise<void> {
  const id = co.id?.trim();
  if (!id) throw new Error('Change order id is required');
  if (co.status !== 'sent') {
    throw new Error('Only sent change orders can be retracted. Use void for approved change orders.');
  }
  const entry: ChangeOrderAuditEntry = {
    at: new Date(),
    byUid: actor.uid,
    byName: actor.name,
    action: 'retracted',
    fromStatus: 'sent',
    toStatus: 'draft',
    amount: co.deltaAmount,
  };
  await updateDoc(doc(db, CHANGE_ORDERS_COLLECTION, id), {
    status: 'draft',
    sentAt: deleteField(),
    auditLog: [...(co.auditLog ?? []), { ...entry, at: Timestamp.fromDate(entry.at) }],
    updatedAt: Timestamp.now(),
  });
}

/** Delete a draft change order (only drafts should be deletable). */
export async function deleteChangeOrder(id: string): Promise<void> {
  await deleteDoc(doc(db, CHANGE_ORDERS_COLLECTION, id));
}
